/**
 * The app half of the voice UI-action appliers' injected port: bound HERE, once,
 * not at each applier's call site.
 *
 * Every member is an app-shell write the appliers used to perform themselves —
 * the classic-Scribe route push, the session-window tab focus, the V1
 * side-panel pair, the choose-structure modal pair, the voice workspace's
 * minimise and the non-React message formatter. The appliers now take
 * `VoiceUiActionsPort` as a parameter, so this module is the only place in the
 * tree that knows which app module answers each one.
 *
 * The bound re-exports below are name-intact. App callers import them from
 * here; the old path under `src/features/voice-assistant/hooks/` is a pure
 * re-export shim of the mount hook.
 *
 * `Tab` and `TabType` do not cross: the sibling `voice-session-tab-focus.ts`
 * maps the payload here on both sides of the applier's decision, which is why
 * the port carries a pure name resolve beside the effect.
 */
import {
  isChooseNoteStructureModalDismissibleAtom,
  isChooseNoteStructureModalOpenAtom,
} from '@/atoms/noteStructureAtoms';
import { activePanelAtom, embeddingsPanelSessionIdAtom } from '@/atoms/panelAtoms';
import {
  isWorkPreviewArtifact,
  mapUiOpenArtifactData,
} from '@/features/agent-events/ui-tools/map-open-artifact';
import { refreshArtifactData } from '@/features/projects/components/refreshArtifactData';
import { useVoiceAssistantStore } from '@/features/voice-assistant/store/useVoiceAssistantStore';
import { formatMessageOutsideReact } from '@/utils/i18n/intl-bridge';
import { reactQueryClient } from '@/utils/reactQueryClient';
import type {
  VoiceSidePanel,
  VoiceUiActionPayload,
  VoiceUiActionsPort,
} from '@heidi/agents';
import {
  applyClassicScribeOpenArtifact as applyClassicScribeOpenArtifactSurface,
  applySessionSidePanel as applySessionSidePanelSurface,
  applyUiAction as applyUiActionSurface,
  openChooseStructurePicker as openChooseStructurePickerSurface,
  openTemplatePicker as openTemplatePickerSurface,
  requestSessionSidePanel as requestSessionSidePanelSurface,
  useApplyVoiceSessionPanel as useApplyVoiceSessionPanelSurface,
} from '@heidi/agents';
import type { UiOpenArtifactWire } from '@heidi/agents/contracts';
import { getDefaultStore } from 'jotai';
import Router from 'next/router';

import { requestSessionTabFocus, tabFromOpenArtifact } from './voice-session-tab-focus';

/** Binds today's app implementations to the appliers' `VoiceUiActionsPort`. */
export const voiceUiActionsPort: VoiceUiActionsPort = {
  // `NavigatePort` resolves after the route settles; the router's own boolean is
  // not part of the contract, so it is awaited and dropped here.
  onNavigate: async (href) => {
    await Router.push(href);
  },
  resolveSessionTabName: (data) => tabFromOpenArtifact(data)?.name ?? null,
  focusSessionTab: (sessionId, data) => {
    const tab = tabFromOpenArtifact(data);
    if (tab) requestSessionTabFocus(sessionId, tab);
  },
  setActiveSessionPanel: (panel) => getDefaultStore().set(activePanelAtom, panel),
  setEmbeddingsPanelSessionId: (sessionId) =>
    getDefaultStore().set(embeddingsPanelSessionIdAtom, sessionId),
  setChooseNoteStructureModalDismissible: (dismissible) =>
    getDefaultStore().set(isChooseNoteStructureModalDismissibleAtom, dismissible),
  setChooseNoteStructureModalOpen: (open) =>
    getDefaultStore().set(isChooseNoteStructureModalOpenAtom, open),
  minimizeVoiceWorkspace: () => useVoiceAssistantStore.getState().minimizeWorkspace(),
  formatMessage: (descriptor) => formatMessageOutsideReact(descriptor),
  currentPathname: () => Router.pathname,
  mapOpenArtifact: (data) => mapUiOpenArtifactData(data),
  isWorkPreviewArtifact: (artifact) => isWorkPreviewArtifact(artifact),
  refreshArtifact: (artifact) => refreshArtifactData(artifact, reactQueryClient),
};

export const applyUiAction = (payload: VoiceUiActionPayload): boolean =>
  applyUiActionSurface(payload, voiceUiActionsPort);

export const applyClassicScribeOpenArtifact = (data: UiOpenArtifactWire): boolean =>
  applyClassicScribeOpenArtifactSurface(data, voiceUiActionsPort);

export const applySessionSidePanel = (sessionId: string, panel: VoiceSidePanel): void =>
  applySessionSidePanelSurface(sessionId, panel, voiceUiActionsPort);

export const requestSessionSidePanel = (sessionId: string, panel: VoiceSidePanel): void =>
  requestSessionSidePanelSurface(sessionId, panel, voiceUiActionsPort);

export const openChooseStructurePicker = (): void =>
  openChooseStructurePickerSurface(voiceUiActionsPort);

export const openTemplatePicker = (): boolean =>
  openTemplatePickerSurface(voiceUiActionsPort);

/**
 * The mount hook binds HERE, not at `GlobalVoiceAssistant.tsx`: a mount holding
 * the port makes the old path a bodied delegate, which under a locked root is a
 * `modules[]` entry — the shape the tenth intake measured as retiring nothing.
 */
export const useApplyVoiceSessionPanel = (): void =>
  useApplyVoiceSessionPanelSurface(voiceUiActionsPort);
